import { computed, unref, type ComputedRef, type Ref } from 'vue';

export interface PriceUnitFormatted {
    label: string;
    unit_single: string;
    unit_plural: string;
}

/**
 * `price_unit_formatted` does not have one shape across the API. The packing-family resources
 * (`BookingPackingResource` / `BookingUnpackingResource`) send the object above, while
 * `BookingStorageProductResource` sends a bare string from `PeriodType::asNoun()` ('day' | 'week' |
 * 'month'). Anything reading the field has to tolerate both.
 */
export type PriceUnitFormattedInput = PriceUnitFormatted | string | null;

export interface ProductData {
    // For FIXED deposits this is a Money-like object from backend
    // For METERED deposits this will be null
    deposit_amount?: unknown | null;
    // For METERED deposits this represents the quantity (e.g. hours, boxes)
    // For FIXED deposits this will be null
    deposit_quantity?: number | null;
    deposit_type?: string;
    // Final billed quantity, set once a VARIABLE service completes. Null until then.
    quantity?: number | null;
    price_unit?: string;
    price_unit_formatted?: PriceUnitFormatted;
}

export interface PriceUnitFormatting {
    depositQuantity: ComputedRef<string | number>;
    depositUnitLabel: ComputedRef<string>;
    finalQuantity: ComputedRef<string | number>;
    finalQuantityLabel: ComputedRef<string>;
}

/**
 * Both `deposit_quantity` and `quantity` are stored in the service's own `price_unit` — so a
 * HALF_HOUR service counts half-hours, because `price_amount` is the per-30-min rate. Customers
 * think in hours, so halve for display.
 */
function toDisplayQuantity(raw: number | null | undefined, priceUnit?: string): number | null {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;

    return priceUnit === 'HALF_HOUR' ? raw / 2 : raw;
}

function toUnitLabel(displayed: number, priceUnit?: string, formatted?: PriceUnitFormatted): string {
    // The stored unit is half-hours, but the displayed figure is hours — so the label has to
    // come from the conversion, not from `price_unit_formatted` (which would read "30 mins").
    if (priceUnit === 'HALF_HOUR') {
        return displayed === 1 ? 'hour' : 'hours';
    }

    if (!formatted) return '';

    return displayed === 1 ? formatted.unit_single : formatted.unit_plural;
}

/**
 * Resolve the trailing unit for a "$X / unit" price line, e.g. `/ hour` or `/ box`.
 *
 * Returns null when there is no unit to show, so callers can omit the separator entirely (a fixed
 * price has no unit). Unlike `toUnitLabel` this is quantity-free — it always reads the singular
 * form, because the figure it annotates is a per-one-unit rate.
 *
 * Handles both `price_unit_formatted` shapes (see PriceUnitFormattedInput). Interpolating the
 * object form directly is what rendered "$616.00 / [object Object]" on the confirmation page for
 * an hourly packing service.
 */
export function priceUnitSuffix(priceUnit?: string, formatted?: PriceUnitFormattedInput): string | null {
    // The stored rate is per 30 minutes, so the unit is too — regardless of what the backend
    // labelled it. Kept ahead of the lookup so it still holds when `formatted` is absent.
    if (priceUnit === 'HALF_HOUR') {
        return '/ 30 min';
    }

    const unit = priceUnitName(formatted);

    return unit ? `/ ${unit}` : null;
}

/**
 * The bare singular unit name ('hour', 'box', 'day'), normalised across both
 * `price_unit_formatted` shapes. Null when there is nothing to show.
 */
export function priceUnitName(formatted?: PriceUnitFormattedInput): string | null {
    const unit = typeof formatted === 'string' ? formatted : formatted?.unit_single;

    return unit || null;
}

/**
 * True when the service quotes one total rather than a rate. A Fixed quote has no unit, so nothing
 * that shows a price should name one, even when an older save left `price_unit` behind
 * (CU-14zcqntq2bh: a Fixed $960 packing read "$960.00 / box").
 *
 * Written as "is Fixed" rather than "is not Variable" on purpose: storage sends no `price_type`, and
 * its billing period ("/ month") is a real rate that must keep showing.
 */
export function quotesAFixedPrice(product?: { price_type?: string | null } | null): boolean {
    return product?.price_type === 'FIXED';
}

export function usePriceUnitFormatting(
    productData: Ref<ProductData> | ComputedRef<ProductData> | ProductData,
): PriceUnitFormatting {
    const data = computed(() => {
        return unref(productData);
    });

    const depositQuantity = computed(() => {
        const product = data.value;

        // Only apply quantity/unit formatting for METERED deposits
        if (product?.deposit_type === 'METERED') {
            return toDisplayQuantity(product.deposit_quantity, product.price_unit) ?? '-';
        }
        return '-';
    });

    const depositUnitLabel = computed(() => {
        const product = data.value;

        // Only provide unit labels for METERED deposits
        if (product?.deposit_type !== 'METERED' || !product || !product.price_unit_formatted) return '';

        // Use the displayed quantity for pluralization instead of raw deposit_quantity
        return toUnitLabel(Number(depositQuantity.value), product.price_unit, product.price_unit_formatted);
    });

    const finalQuantity = computed(() => {
        const product = data.value;

        return toDisplayQuantity(product?.quantity, product?.price_unit) ?? '-';
    });

    const finalQuantityLabel = computed(() => {
        const product = data.value;

        if (!product || typeof product.quantity !== 'number') return '';

        return toUnitLabel(Number(finalQuantity.value), product.price_unit, product.price_unit_formatted);
    });

    return {
        depositQuantity,
        depositUnitLabel,
        finalQuantity,
        finalQuantityLabel,
    };
}
